import { useEffect, useState } from 'react';
import {
  getPoolShipping,
  type ApiClient,
  type IframePoolShippingDetail,
} from '../../../modules/api';
import { isRefundedShipment } from '../../../modules/shippingPromo';
import { useWallet } from '../../../modules/wallet/useWallet';
import { DeliveryCard, DeliveryPageShell } from '../shared';
import { SuccessSection } from './sections/SuccessSection';
import { RefundNoticeSection } from './sections/RefundNoticeSection';
import { AddressSection } from './sections/AddressSection';
import { ServiceSection } from './sections/ServiceSection';
import { ItemsSection } from './sections/ItemsSection';
import { PaymentSection } from './sections/PaymentSection';
import { CancelDeliveryButton } from './CancelDeliveryButton';
import { CancelDeliveryModal } from './CancelDeliveryModal';

export interface DeliveryShowPageProps {
  client: ApiClient;
  shippingId: number;
  /** Top-bar "< Home" → return to My Packs (Deliveries tab). */
  onBack: () => void;
  /** Drop the noise-SVG overlay from the page gradient. */
  hideBackgroundPattern?: boolean;
}

/**
 * Delivery Show page (Figma 361:132067) — read-only view of an existing
 * pool_shipping. Two stacked glass cards:
 *   1. Success / Support — confirmation copy + "Ask a Question" CTA.
 *   2. Details — address, chosen service + paid positions, item grid.
 *
 * Data comes from the mocked `getPoolShipping(id)` — see TODO in the
 * api module.
 */
export function DeliveryShowPage({
  client,
  shippingId,
  onBack,
  hideBackgroundPattern = false,
}: DeliveryShowPageProps) {
  const [shipping, setShipping] = useState<IframePoolShippingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { refresh: refreshWallet } = useWallet();
  const [cancelOpen, setCancelOpen] = useState(false);
  // The cancel window closed while the confirm modal was open — show why the
  // button disappeared.
  const [cancelWindowClosed, setCancelWindowClosed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getPoolShipping(client, shippingId)
      .then((res) => {
        if (cancelled) return;
        setShipping(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load delivery');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, shippingId]);

  if (loading) {
    return (
      <DeliveryPageShell
        client={client}
        title="Delivery Information"
        onBack={onBack}
        hideBackgroundPattern={hideBackgroundPattern}
      >
        <DeliveryCard>
          <p className="text-[14px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Loading delivery…
          </p>
        </DeliveryCard>
      </DeliveryPageShell>
    );
  }

  if (error || !shipping) {
    return (
      <DeliveryPageShell
        client={client}
        title="Delivery Information"
        onBack={onBack}
        hideBackgroundPattern={hideBackgroundPattern}
      >
        <DeliveryCard>
          <p role="alert" className="text-[14px]" style={{ color: '#F87171' }}>
            {error ?? 'Delivery not found.'}
          </p>
        </DeliveryCard>
      </DeliveryPageShell>
    );
  }

  // Refund notice is driven by the per-item settle statuses, not by
  // `settleSummary`: the backend keeps the summary null while refunded lines
  // are mixed with still-processing ones, but the player must already see
  // which cards were refunded.
  const refundedTitles = shipping.items
    .filter((it) => it.settleStatus === 'unavailable')
    .map((it) => it.title);
  const refundVariant =
    shipping.items.length > 0 && refundedTitles.length === shipping.items.length
      ? 'refunded'
      : 'partially_shipped';

  const handleCanceled = (updated: IframePoolShippingDetail) => {
    // The cancel response is the fresh Show payload — no refetch needed
    // (this page doesn't poll). The refund lands on the wallet balance.
    setShipping(updated);
    setCancelOpen(false);
    // The buyback credit and the shipping refund are queued, not sent yet.
    void refreshWallet({ expectCredit: true });
  };

  const handleNotCancelable = () => {
    setCancelOpen(false);
    setCancelWindowClosed(true);
    // Reflect the fresh backend state (the button disappears).
    void getPoolShipping(client, shippingId)
      .then(setShipping)
      .catch(() => {});
  };

  return (
    <DeliveryPageShell client={client} title="Delivery Information" onBack={onBack}>
      <div className="flex flex-col gap-4">
        <DeliveryCard>
          {refundedTitles.length > 0 ? (
            <RefundNoticeSection
              variant={refundVariant}
              refundedTitles={refundedTitles}
              trackingNumber={shipping.trackingNumber}
              companyName={shipping.companyName}
            />
          ) : (
            <SuccessSection
              status={shipping.status}
              trackingNumber={shipping.trackingNumber}
              companyName={shipping.companyName}
            />
          )}
        </DeliveryCard>

        <DeliveryCard>
          <div className="flex flex-col gap-6">
            <AddressSection address={shipping.address} />
            <ServiceSection
              service={shipping.service}
              positions={shipping.positions}
            />
            {shipping.chargeBreakdown && (
              <PaymentSection
                charge={shipping.chargeBreakdown}
                refunded={isRefundedShipment(shipping)}
              />
            )}
            <ItemsSection items={shipping.items} />
          </div>
        </DeliveryCard>

        {cancelWindowClosed && (
          <DeliveryCard>
            <p
              role="alert"
              className="text-[14px] leading-[20px]"
              style={{ color: 'var(--co-ShippingItemStatus-refundedFg, #FFB020)' }}
            >
              This delivery can no longer be canceled — it is already being
              prepared for shipment.
            </p>
          </DeliveryCard>
        )}

        {shipping.isAllowToCancel && !cancelWindowClosed && (
          <CancelDeliveryButton onClick={() => setCancelOpen(true)} />
        )}
      </div>

      {cancelOpen && (
        <CancelDeliveryModal
          client={client}
          shippingId={shipping.id}
          onClose={() => setCancelOpen(false)}
          onCanceled={handleCanceled}
          onNotCancelable={handleNotCancelable}
        />
      )}
    </DeliveryPageShell>
  );
}
